/**
 * REGISTRO de paneles de conducción de la Cabina (ADR-0005 §6).
 *
 * Mismo patrón que el registro de primitivas del Bolsillo: el manifiesto
 * declara `conduccion.panel` y la Cabina resuelve el componente acá — sumar
 * un control de conducción nuevo (rotar pares, responder en vivo, marcar
 * severidad) es escribir su componente y registrarlo, sin tocar CabinaPage.
 *
 * Dos paneles son AUTOMÁTICOS y siempre se evalúan (se auto-ocultan si no
 * aplican): la ronda por secciones (capturas[].secciones) y el supervoto del
 * decisor (config.supervoto). Un panel declarado que no está en el registro
 * se muestra como aviso a quien conduce — nunca una Cabina muda en silencio.
 */
import type { ComponentType } from 'react';
import { AlertCircle } from 'lucide-react';
import type { ActividadDetalle } from '../../lib/sesion';
import { Supervoto } from './Supervoto';
import { RondaControl } from './RondaControl';
import { RespuestaEnVivo } from './RespuestaEnVivo';
import { MicrofonoSala } from './MicrofonoSala';
import { ProyeccionControl } from './ProyeccionControl';

export interface PropsPanel {
  actividad: ActividadDetalle;
  puedeConducir: boolean;
}

/** Paneles que un manifiesto puede declarar por nombre en `conduccion.panel`. */
const PANELES: Record<string, ComponentType<PropsPanel>> = {
  ronda: RondaControl,
  supervoto: Supervoto,
  responder: RespuestaEnVivo,
  microfono_sala: MicrofonoSala,
};

/** Los que se evalúan SIEMPRE (gating propio); no se duplican si se declaran. */
const AUTOMATICOS = new Set(['ronda', 'supervoto', 'microfono_sala']);

export function PanelConduccion({ actividad, puedeConducir }: PropsPanel) {
  const declarado = actividad.herramienta.conduccion?.panel ?? null;
  const Extra =
    declarado && !AUTOMATICOS.has(declarado) ? (PANELES[declarado] ?? null) : null;

  return (
    <>
      {/* Qué proyecta la Pantalla (explicar/tablero): sirve a CUALQUIER
          actividad en curso, por eso es automático y no se declara. */}
      <ProyeccionControl actividad={actividad} puedeConducir={puedeConducir} />
      <RondaControl actividad={actividad} puedeConducir={puedeConducir} />
      <Supervoto actividad={actividad} puedeConducir={puedeConducir} />
      {/* Canal de soporte del registro: sirve a CUALQUIER conversación, por
          eso es automático y no se declara por manifiesto. */}
      <MicrofonoSala actividad={actividad} puedeConducir={puedeConducir} />
      {Extra && <Extra actividad={actividad} puedeConducir={puedeConducir} />}
      {declarado && !AUTOMATICOS.has(declarado) && !Extra && puedeConducir && (
        <p className="flex items-center gap-1.5 rounded-md border border-warning px-3 py-2 font-sans text-sm text-warning">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          Esta herramienta declara el panel «{declarado}», que esta versión de la
          Cabina todavía no trae. La captura del Bolsillo funciona igual.
        </p>
      )}
    </>
  );
}
